import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, MapPin, Plus, Wallet } from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CommercialGate from "../../components/commercial/CommercialGate";
import { api } from "../../lib/api";
import { toast } from "sonner";
import BusinessVerification from "../../components/commercial/BusinessVerification";
import { fmtCents, setPageTitle } from "../../lib/format";
import { fadeUp, revealOnce, staggerParent } from "../../lib/motion";
import { motion } from "framer-motion";

function OccupancyRing({ value }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const v = Math.min(1, Math.max(0, value || 0));
  return (
    <svg viewBox="0 0 84 84" className="w-20 h-20" role="img" aria-label={`Occupancy ${Math.round(v * 100)}%`} data-testid="occupancy-ring">
      <circle cx="42" cy="42" r={r} fill="none" stroke="#1F2328" strokeWidth="8" />
      <circle cx="42" cy="42" r={r} fill="none" stroke="#E50914" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={c * (1 - v)} transform="rotate(-90 42 42)"
              className="transition-[stroke-dashoffset] duration-700" />
      <text x="42" y="47" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="700">{Math.round(v * 100)}%</text>
    </svg>
  );
}

const STATUS_CHIP = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  unpublished: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  suspended: "bg-red-500/15 text-red-400 border-red-500/30",
};

function DashboardBody() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setPageTitle("Commercial Dashboard");
    api.commercialDashboard().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) {
    return <div className="card-dark p-8 text-center text-sm text-[#FF6B6B]" role="alert" data-testid="commercial-dash-error">{error}</div>;
  }
  if (!data) {
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={i} className="card-dark h-28 animate-pulse" />)}</div>;
  }

  const t = data.totals;
  const f = data.financials;
  const kpis = [
    ["Properties", t.properties], ["Parking spots", t.spots_total], ["Available now", t.available],
    ["Occupied", t.occupied], ["Reserved / upcoming", t.reserved_upcoming], ["Bookings today", t.today_bookings],
    ["Pending earnings", fmtCents(f.wallet_pending_cents)], ["Available earnings", fmtCents(f.wallet_available_cents)],
  ];

  return (
    <>
      {t.properties === 0 ? (
        <div className="card-dark p-12 text-center" data-testid="commercial-dash-empty">
          <Building2 className="w-10 h-10 mx-auto text-[#FF3B45]" />
          <h2 className="type-h3 mt-4">No properties yet</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">Create your first commercial property to get a property code, QR sign, categories and rate plans.</p>
          <Link to="/commercial/properties/new" className="btn-primary inline-flex mt-6 text-sm" data-testid="commercial-first-property">
            Create your first property <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <>
          <motion.div variants={staggerParent(0.06)} {...revealOnce} className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="commercial-kpis">
            {kpis.map(([label, value]) => (
              <motion.div key={label} variants={fadeUp} className="card-dark p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="meta-label">{label}</p>
                <p className="font-display font-bold text-2xl mt-2">{value}</p>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <div className="card-dark p-6 flex items-center gap-6">
              <OccupancyRing value={t.occupancy} />
              <div>
                <p className="meta-label">Live occupancy</p>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{t.occupied} of {t.spots_total} spots currently occupied across {t.properties} {t.properties === 1 ? "property" : "properties"}.</p>
              </div>
            </div>
            <div className="card-dark p-6 lg:col-span-2">
              <p className="meta-label">Gross revenue · fees · discounts</p>
              <div className="mt-3 grid grid-cols-3 gap-4">
                <div><p className="font-display font-bold text-xl">{fmtCents(f.gross_cents)}</p><p className="text-[11px] text-zinc-500 mt-1">Gross (paid bookings)</p></div>
                <div><p className="font-display font-bold text-xl">{fmtCents(f.host_fee_cents)}</p><p className="text-[11px] text-zinc-500 mt-1">Service fees</p></div>
                <div><p className="font-display font-bold text-xl">{fmtCents(f.discount_cents)}</p><p className="text-[11px] text-zinc-500 mt-1">Discounts</p></div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-4 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Payouts run through your existing wallet &amp; Stripe Connect.</p>
            </div>
          </div>

          <div className="mt-6 space-y-3" data-testid="commercial-property-breakdown">
            <p className="section-label">Property breakdown</p>
            {data.properties.map((p) => (
              <Link key={p.property_id} to={`/commercial/properties/${p.property_id}`} data-testid={`dash-property-${p.property_code}`}
                    className="card-dark p-5 flex flex-wrap items-center gap-4 hover:border-[#3F3F46] transition-colors group">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <p className="font-display font-bold">{p.name}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CHIP[p.status] || STATUS_CHIP.draft}`}>{p.status}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Code {p.property_code} · {p.categories} categories</p>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center"><p className="font-bold">{p.spots_active}</p><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Spots</p></div>
                  <div className="text-center"><p className="font-bold text-emerald-400">{p.available}</p><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Open</p></div>
                  <div className="text-center"><p className="font-bold text-[#FF3B45]">{p.occupied}</p><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Occupied</p></div>
                  <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF3B45] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function ReportCard() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    api.commercialMonthlyReport(month).then((r) => setRows(r.properties)).catch((e) => toast.error(e.message)).finally(() => setBusy(false));
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commercial/reports/monthly?month=${month}&format=csv`, { credentials: "include" });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `myredpark-report-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mt-6 card-dark p-6" data-testid="monthly-report">
      <div className="flex flex-wrap items-center gap-3">
        <p className="section-label !mb-0">Monthly report</p>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
               className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3 py-1.5 text-xs text-white" data-testid="report-month" aria-label="Report month" />
        <button onClick={exportCsv} className="btn-secondary !py-1.5 !px-3 text-xs ml-auto" data-testid="report-export-csv">Export CSV</button>
      </div>
      <div className="mt-4 space-y-2">
        {busy && <div className="h-9 rounded-xl bg-white/[0.03] animate-pulse" />}
        {!busy && rows && rows.length === 0 && <p className="text-xs text-zinc-600">No properties to report for {month}.</p>}
        {!busy && rows && rows.map((r) => (
          <div key={r.property_id} className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs rounded-xl bg-white/[0.03] px-4 py-3" data-testid={`report-row-${r.property_code}`}>
            <span className="font-semibold text-sm">{r.name}</span>
            <span className="text-zinc-500">{r.paid_bookings} paid / {r.bookings} total</span>
            <span className="text-zinc-400">Gross {fmtCents(r.gross_cents)}</span>
            <span className="text-zinc-400">Fees {fmtCents(r.service_fee_cents)}</span>
            <span className="text-zinc-400">Discounts {fmtCents(r.discount_cents)}</span>
            <span className="text-emerald-400 font-semibold">Net {fmtCents(r.net_cents)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CommercialDashboard() {
  return (
    <CommercialGate>
      <div className="min-h-screen flex flex-col" data-testid="commercial-dashboard">
        <Navbar />
        <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="section-label">Commercial</p>
              <h1 className="type-display mt-3">Dashboard</h1>
            </div>
            <Link to="/commercial/properties/new" className="btn-primary text-sm" data-testid="commercial-new-property">
              <Plus className="w-4 h-4" /> New property
            </Link>
          </div>
          <div className="mt-8"><DashboardBody /></div>
          <div className="mt-6"><BusinessVerification /></div>
          <ReportCard />
        </main>
        <Footer />
      </div>
    </CommercialGate>
  );
}
